import { useMemo, useRef, useState, useEffect } from "react";
import type { Lead, LeadFollowUp } from "@/types/lead";
import {
  cacheRtoTermsForOrg,
  loadRtoTermsForOrg,
  type RtoTermDefinition,
} from "@/lib/rto/rtoTermSettings";
import { getOpsSettings, patchOpsSettings, rtoTermDefinitionsFromOpsSettingsResponse } from "@/lib/opsPaperworkClient";
import { SalesConsoleDesktopLayout } from "./SalesConsoleDesktopLayout";
import { SalesConsoleFollowUpSection } from "./SalesConsoleFollowUpSection";
import { SalesConsoleCustomerCalendarSection } from "./SalesConsoleCustomerCalendarSection";
import { SalesConsoleDealTodosSection } from "./SalesConsoleDealTodosSection";
import { SalesConsoleStickyNotesSection } from "./SalesConsoleStickyNotesSection";
import { SalesConsoleBulkCaptureSection } from "./SalesConsoleBulkCaptureSection";
import { SalesConsoleRtoCalculatorSection } from "./SalesConsoleRtoCalculatorSection";
import { useSalesConsoleVoiceCapture } from "./useSalesConsoleVoiceCapture";
import { useSalesConsoleLeadBuckets } from "./useSalesConsoleLeadBuckets";
import {
  MAX_STICKY_NOTES,
  normalizeStickyNotes,
  parseStickyNotes,
  payoffStateStorageKey,
  resolveSalesConsoleRtoDisplayTitle,
  type StickyNote,
} from "./salesConsoleUtils";

type Props = {
  leads: Lead[];
  onViewLead: (lead: Lead) => void;
  onBulkImport: (notes: string) => Promise<{ count: number; names: string[] }>;
  onUpdateFollowUpStatus: (leadId: string, followUpId: string, status: LeadFollowUp["status"]) => void;
  onCreateFollowUp: (leadId: string, draft: { next_action: string; due_date: string; reason: string }) => void;
  onSaveFollowUp: (
    leadId: string,
    followUpId: string,
    patch: Partial<{ next_action: string; due_date: string; reason: string; status: LeadFollowUp["status"] }>
  ) => void;
  organizationId: string | null;
  canEditStickyNote: boolean;
};
export function SalesConsolePanel({
  leads,
  onViewLead,
  onBulkImport,
  onUpdateFollowUpStatus,
  onCreateFollowUp,
  onSaveFollowUp,
  organizationId,
  canEditStickyNote,
}: Props) {
  const [bulkNotes, setBulkNotes] = useState("");
  const [consoleLeadId, setConsoleLeadId] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkCaptureMsg, setBulkCaptureMsg] = useState<{ tone: "ok" | "warn"; text: string } | null>(null);
  const [cashPrice, setCashPrice] = useState(10000);
  const [cashPaidToday, setCashPaidToday] = useState(500);
  const [taxRate, setTaxRate] = useState(7);
  const [payoffState, setPayoffState] = useState("");
  const [openEpoForTermId, setOpenEpoForTermId] = useState<string | null>(null);
  const [dbRows, setDbRows] = useState<RtoTermDefinition[] | null>(null);
  const [stickyNotes, setStickyNotes] = useState<StickyNote[]>([]);
  const [stickyNotesDraft, setStickyNotesDraft] = useState<StickyNote[]>([]);
  const [stickySaving, setStickySaving] = useState(false);
  const [stickyMsg, setStickyMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [rtoTitle, setRtoTitle] = useState("RTO term calculator (v1.0)");
  const stickyNotesRef = useRef<StickyNote[]>([]);
  const stickyNotesDraftRef = useRef<StickyNote[]>([]);
  const { voiceBusy, voiceHint, voiceHintTone, stopVoiceCapture, onTranscribeIntoBulkNotes, cleanupVoiceCapture } =
    useSalesConsoleVoiceCapture({ setBulkNotes });
  const { followUpsNeedingAttentionCount, needsReviewLeads, openFollowUps, overdue, upcoming } =
    useSalesConsoleLeadBuckets(leads);
  const consoleLeadOptions = useMemo(
    () =>
      leads
        .filter((l) => !l.isTest)
        .sort((a, b) => b.lastUpdated - a.lastUpdated)
        .slice(0, 40),
    [leads],
  );
  const consoleSelectedLead = useMemo(
    () => consoleLeadOptions.find((l) => l.id === consoleLeadId) ?? null,
    [consoleLeadOptions, consoleLeadId],
  );
  const rows = useMemo<RtoTermDefinition[]>(
    () => dbRows ?? (organizationId ? loadRtoTermsForOrg(organizationId) : []),
    [dbRows, organizationId]
  );
  const payoffStates = useMemo(() => {
    const out = new Set<string>();
    for (const row of rows) {
      const map = row.early_purchase_percent_by_state ?? {};
      for (const key of Object.keys(map)) {
        const code = key.trim().toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2);
        if (code) out.add(code);
      }
    }
    return Array.from(out).sort();
  }, [rows]);
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(payoffStateStorageKey(organizationId));
      const normalized = saved ? saved.trim().toUpperCase() : "";
      if (normalized) setPayoffState(normalized);
    } catch {
      // ignore localStorage failures
    }
  }, [organizationId]);
  useEffect(() => {
    if (!payoffStates.length) {
      setPayoffState("");
      return;
    }
    if (!payoffState || !payoffStates.includes(payoffState)) {
      setPayoffState(payoffStates[0]);
    }
  }, [payoffState, payoffStates]);
  useEffect(() => {
    if (!payoffState) return;
    try {
      window.localStorage.setItem(payoffStateStorageKey(organizationId), payoffState);
    } catch {
      // ignore localStorage failures
    }
  }, [organizationId, payoffState]);
  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    const load = async () => {
      const res = await getOpsSettings();
      if (!res.ok || !res.data || cancelled) return;
      const settings = (res.data.settings ?? {}) as Record<string, unknown>;
      const nextStickyNotes = parseStickyNotes(settings);
      const nextRtoTitle = resolveSalesConsoleRtoDisplayTitle(res.data as Record<string, unknown>);
      setStickyNotes(nextStickyNotes);
      const hasUnsavedStickyDraft =
        JSON.stringify(normalizeStickyNotes(stickyNotesDraftRef.current)) !==
        JSON.stringify(normalizeStickyNotes(stickyNotesRef.current));
      // Avoid clobbering trainer/admin draft edits during background refresh.
      if (!hasUnsavedStickyDraft || !canEditStickyNote) {
        setStickyNotesDraft(nextStickyNotes);
      }
      setRtoTitle(nextRtoTitle);
      const parsed = rtoTermDefinitionsFromOpsSettingsResponse(res.data as Record<string, unknown>);
      if (!parsed.length) return;
      if (cancelled) return;
      setDbRows(parsed);
      cacheRtoTermsForOrg(organizationId, parsed);
    };
    void load();
    const periodicRefresh = window.setInterval(() => {
      void load();
    }, 300000);
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      void load();
    };
    const onStickySaved = (ev: Event) => {
      const ce = ev as CustomEvent<{ organizationId?: string }>;
      if (ce.detail?.organizationId !== organizationId) return;
      void load();
    };
    const onRtoSaved = (ev: Event) => {
      const ce = ev as CustomEvent<{ organizationId?: string }>;
      if (ce.detail?.organizationId !== organizationId) return;
      void load();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("sales-console-sticky-note-saved", onStickySaved);
    window.addEventListener("rto-terms-saved", onRtoSaved);
    return () => {
      cancelled = true;
      window.clearInterval(periodicRefresh);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("sales-console-sticky-note-saved", onStickySaved);
      window.removeEventListener("rto-terms-saved", onRtoSaved);
    };
  }, [organizationId, canEditStickyNote]);
  useEffect(() => {
    return () => {
      cleanupVoiceCapture();
    };
  }, [cleanupVoiceCapture]);
  useEffect(() => {
    stickyNotesRef.current = stickyNotes;
  }, [stickyNotes]);
  useEffect(() => {
    stickyNotesDraftRef.current = stickyNotesDraft;
  }, [stickyNotesDraft]);
  const stickyDirty =
    JSON.stringify(normalizeStickyNotes(stickyNotesDraft)) !==
    JSON.stringify(normalizeStickyNotes(stickyNotes));
  async function saveStickyNote() {
    if (!organizationId) return;
    setStickySaving(true);
    setStickyMsg(null);
    const next = normalizeStickyNotes(stickyNotesDraft);
    const title = next[0]?.title || null;
    const body = next.length ? JSON.stringify(next) : null;
    const res = await patchOpsSettings({
      sales_console_sticky_note_title: title,
      sales_console_sticky_note_body: body,
    });
    setStickySaving(false);
    if (!res.ok) {
      setStickyMsg({ ok: false, text: `Sticky note save failed: ${res.error}` });
      return;
    }
    setStickyNotes(next);
    setStickyNotesDraft(next);
    setStickyMsg({ ok: true, text: "Sticky notes saved for this organization." });
    window.dispatchEvent(
      new CustomEvent("sales-console-sticky-note-saved", { detail: { organizationId } })
    );
  }

  return (
    <SalesConsoleDesktopLayout
      followUp={
        <SalesConsoleFollowUpSection
          openCount={openFollowUps.length}
          overdueCount={overdue.length}
          needsAttentionCount={followUpsNeedingAttentionCount}
          needsReviewCount={needsReviewLeads.length}
          overdue={overdue}
          upcoming={upcoming}
          onViewLead={onViewLead}
          onUpdateFollowUpStatus={onUpdateFollowUpStatus}
        />
      }
      calendar={
        <SalesConsoleCustomerCalendarSection
          leads={leads}
          leadId={consoleLeadId}
          onLeadIdChange={setConsoleLeadId}
          onViewLead={onViewLead}
          onCreateFollowUp={onCreateFollowUp}
          onSaveFollowUp={onSaveFollowUp}
        />
      }
      dealTodos={
        <SalesConsoleDealTodosSection selectedLead={consoleSelectedLead} onViewLead={onViewLead} />
      }
      stickyNotes={
        <SalesConsoleStickyNotesSection
          canEditStickyNote={canEditStickyNote}
          stickyNotes={stickyNotes}
          stickyNotesDraft={stickyNotesDraft}
          stickySaving={stickySaving}
          stickyDirty={stickyDirty}
          stickyMsg={stickyMsg}
          maxStickyNotes={MAX_STICKY_NOTES}
          normalizeStickyNotes={normalizeStickyNotes}
          setStickyNotesDraft={setStickyNotesDraft}
          setStickyMsg={setStickyMsg}
          onSaveStickyNote={() => void saveStickyNote()}
        />
      }
      bulkCapture={
        <SalesConsoleBulkCaptureSection
          bulkNotes={bulkNotes}
          bulkBusy={bulkBusy}
          voiceBusy={voiceBusy}
          voiceHint={voiceHint}
          voiceHintTone={voiceHintTone}
          bulkError={bulkError}
          bulkCaptureMsg={bulkCaptureMsg}
          onBulkNotesChange={setBulkNotes}
          onToggleVoice={() => {
            if (voiceBusy) {
              stopVoiceCapture();
              return;
            }
            void onTranscribeIntoBulkNotes();
          }}
          onCaptureLeads={async () => {
            stopVoiceCapture();
            setBulkBusy(true);
            setBulkError("");
            setBulkCaptureMsg(null);
            const { count, names } = await onBulkImport(bulkNotes);
            setBulkBusy(false);
            if (count > 0) {
              setBulkNotes("");
              const preview = names.length <= 3 ? names.join(", ") : `${names.slice(0, 3).join(", ")} + ${names.length - 3} more`;
              setBulkCaptureMsg({
                tone: "ok",
                text: count === 1
                  ? `Created 1 draft: ${preview}. Open Leads mode to review — it is the current lead.`
                  : `Created ${count} drafts: ${preview}. Only the first is the active lead here — open Leads mode to review every draft (none were discarded).`,
              });
            } else {
              setBulkError("No leads were created. Try adding clearer separators or one lead per line.");
            }
          }}
        />
      }
      rtoCalculator={
        <SalesConsoleRtoCalculatorSection
          rtoTitle={rtoTitle}
          payoffStates={payoffStates}
          payoffState={payoffState}
          setPayoffState={setPayoffState}
          cashPrice={cashPrice}
          setCashPrice={setCashPrice}
          cashPaidToday={cashPaidToday}
          setCashPaidToday={setCashPaidToday}
          taxRate={taxRate}
          setTaxRate={setTaxRate}
          rows={rows}
          openEpoForTermId={openEpoForTermId}
          setOpenEpoForTermId={setOpenEpoForTermId}
        />
      }
    />
  );
}
